import { AccessService } from 'src/app/core/services/access.service';
import { getIcon } from 'src/app/shared/helpers/iconBuilder';
import { IconType } from 'src/app/shared/interfaces/icon.interface';
import { BadgeWithIconHistoric, FooterTotals, ResponseTask, TaskData, TaskItem, TaskRowContext } from '../interfaces/task.interface';
import { actionMenuCards } from 'src/app/shared/interfaces/list-cards.interface';
import { GenericResponseList } from 'src/app/shared/interfaces/table.interface';
import { getArrayWithBadgeAndIcon, getRoundBadge, getSimpleBadge, getStateBadge } from 'src/app/shared/helpers/badgesBuilder';
import { setDecimalHours, setTotalHours } from 'src/app/shared/helpers/timeAndHours';
import { enabledToApprove, enabledToDelete, enabledToEdit, enabledToObserve, getBadgeForRequestTypeWithTooltip, getDateBagde, getIconDuplicate, obteniendoBadgeHfinHini, showObserver } from './functions';
import { PROJECTS_STATUS } from './enums';
import * as moment from 'moment';
import { GenericResponse } from 'src/app/shared/interfaces/generic.interface';
import { getTextWithPic } from 'src/app/shared/helpers/textWithPic';
import { IconBadge } from 'src/app/shared/interfaces/badges.interface';
import { COLORS_BADGE } from 'src/app/shared/enums/global-constants';
import { NameWithPic } from 'src/app/shared/interfaces/textWithPic.interface';
import { localDB } from 'src/app/shared/helpers/localStorage';
import { ACTIVITY_STATUS, TIME_TYPE } from 'src/app/shared/enums/task.enum';

function mapTaskRow(
  item: TaskItem,
  _accessService: AccessService,
  sTipo_listado: string,
  errors: TaskData[],
  hasTaskDashboardTeam: boolean
): void {
  item.icErrorTask = getTaskErrorIcon(item, errors);

  const nIdColaborador: number = Number.parseInt(localDB.getItem('idColaborador')) || -1;
  const context: TaskRowContext = {
    accessService: _accessService,
    sTipo_listado,
    hasTaskDashboardTeam,
    nIdColaborador,
    idUsuario: localStorage.getItem('idColaborador'),
    showApprovalButton: _accessService.validarPermisoSlug("REQUEST-APPROVE-ALL") && nIdColaborador != item.nId_Colaborador,
    TaskEdit: getTaskListPermission(item, _accessService, sTipo_listado, "TASK-UPDATE", "TASK-UPDATE-ALL"),
    TaskObserve: getTaskListPermission(item, _accessService, sTipo_listado, "TASK-OBSERVE", "TASK-OBSERVE-ALL"),
    TaskApprove: getTaskListPermission(item, _accessService, sTipo_listado, "TASK-APPROVE", "TASK-APPROVE-ALL"),
    canEdit: sTipo_listado === 'TASK-LIST-ALL'
      ? _accessService.validarPermisoSlug('TASK-UPDATE-ALL')
      : (_accessService.validarPermisoSlug('TASK-UPDATE') || _accessService.validarPermisoSlug('TASK-UPDATE-ALL')),
    obsBadge: showObserver(item.nContador_Obs, sTipo_listado, item.revisado)
  };

  setTaskBadges(item);

  if (item.sEstado == ACTIVITY_STATUS.ELIMINADO.toString()) {
    setDeletedTaskIcons(item);
    return;
  }

  if (sTipo_listado == 'TASK-LIST-OWN') {
    setOwnTaskIcons(item, context);
  } else {
    setListTaskIcons(item, context);
  }
}

function getTaskErrorIcon(item: TaskItem, errors: TaskData[]): IconBadge {
  let icon = getIcon(
    { type: 'material', name: 'warning' },
    '',
    'red',
    false,
    null
  );
  for (const err of errors) {
    if (item.nId === err.nId && !!err.sMensaje_Error) {
      icon = getIcon(
        { type: 'material', name: 'warning' },
        err.sMensaje_Error,
        'red',
        true,
        'algo'
      );
    }
  }
  return icon;
}

function getTaskListPermission(
  item: TaskItem,
  _accessService: AccessService,
  sTipo_listado: string,
  slug: string,
  slugAll: string
): boolean {
  if (sTipo_listado == 'TASK-LIST-TEAM') {
    return !!(_accessService.validarPermisoSlug('REQUEST-APPROVE-ALL') || (_accessService.validarPermisoSlug("TASK-REVISION") && item.revisado && _accessService.validarPermisoSlug(slug)));
  }
  return !!_accessService.validarPermisoSlug(sTipo_listado == 'TASK-LIST-ALL' ? slugAll : slug);
}

function setTaskBadges(item: TaskItem): void {
  const formattedTime = setTotalHours(item.nMinutos);

  item.nId = item.nId.toString();
  item.nameWithPic = getTextWithPic({
    urlPic: item.sUrl_Foto,
    primaryText: { text: item.sNombre_Colaborador }
  });

  item.bgHourStartEnd = obteniendoBadgeHfinHini(
    item.dFecha_Fin,
    item.dFecha_Registro,
    item.dHora_Fin,
    item.dHora_Inicio
  ) || '';

  item.bgAmount = getSimpleBadge(formattedTime) || '';
  item.hoursColab = formattedTime;

  item.icHistorico = item.nContador_Historico
    ? getIcon(
      { type: 'material', name: 'history' },
      'Histórico de cambios',
      'main',
      true,
      'historic'
    )
    : getEmptyIcon();

  item.badgeDateWithIconHistoric = getArrayWithBadgeAndIcon(
    {
      label: moment(item.dFecha_Registro).format('DD MMM YYYY'),
      tooltip: "",
      color: "main",
      label2: moment(item.dFecha_Registro).format('dddd')
    },
    item.nContador_Historico
      ? {
        icon: {
          type: 'material',
          name: 'history'
        },
        tooltip: "Histórico de cambios",
        action: "tareo-historic",
        typeAction: "modal",
        color: COLORS_BADGE.main,
      }
      : null
  ).filter(
    (badge): badge is { type: string; data: BadgeWithIconHistoric } =>
      badge.type === 'badge' || badge.type === 'badgeDate'
  ).map(badge => ({
    type: badge.type,
    css: badge.data.css,
    firstLabel: badge.data.firstLabel,
    twoLabel: badge.data.twoLabel || '',
    tooltip: badge.data.tooltip
  }));

  item.bgStateType = getStateBadge(item.sNombre_Estado, getStateTooltip(item)) || '';

  item.bgHourType = getBadgeForRequestTypeWithTooltip(
    item.sCodigo_Tipo_Horas,
    item.sNombre_Tipo_Horas,
    item.sNombre_Tipo_Horas
  ) || '';
}

function getStateTooltip(item: TaskItem): string {
  const taskResponse = item as unknown as ResponseTask;
  const fechaCreacion = taskResponse.dFechaCreacion
    ? moment(taskResponse.dFechaCreacion).format('DD/MM/YYYY')
    : 'N/A';

  let fechaModificacion = '';
  if (taskResponse.dFechaModificacion) {
    const fechaMoment = moment(taskResponse.dFechaModificacion);
    const fechaDefault = moment('0001-01-01');
    if (!fechaMoment.isSame(fechaDefault)) {
      fechaModificacion = fechaMoment.format('DD/MM/YYYY');
    }
  }

  let estadoTooltip = "";
  if (
    item.sNombre_Tipo_Horas === "EXTRAS" &&
    item.nEstado_Pago === 1 &&
    item.sNombre_Estado === "APROBADO"
  ) {
    estadoTooltip += `Estado de pago: PAGADO\n`;
  }
  estadoTooltip += `Estado actividad: ${item.sNombre_Estado}\nFecha creación: ${fechaCreacion}`;
  if (fechaModificacion) {
    estadoTooltip += `\nFecha modificación: ${fechaModificacion}`;
  }
  return estadoTooltip;
}

function setDeletedTaskIcons(item: TaskItem): void {
  item.icEditarTarea = getEmptyIcon();
  item.icDuplicarTarea = getEmptyIcon();
  item.icEliminarTarea = getEmptyIcon();
  item.icObservarTarea = getEmptyIcon();
  item.icObservations = getEmptyIcon();
  item.icAprobarTarea = getEmptyIcon();
  item.icAprobarPago = getEmptyIcon();
  item.actionMenuCards = [];
}

function getEditIcon(): IconBadge {
  return getIcon(
    { name: 'edit', type: 'material' },
    'Editar',
    'main',
    true,
    'update'
  );
}

function getDetailIcon(canDetail: boolean): IconBadge {
  return canDetail ? getIcon(
    { name: 'visibility', type: 'material' },
    'Ver detalle',
    'main',
    true,
    'detail'
  ) : getEmptyIcon();
}

function getEditOrDetailIcon(canEdit: boolean, canDetail: boolean): IconBadge {
  return canEdit ? getEditIcon() : getDetailIcon(canDetail);
}

function getDuplicateIcon(): IconBadge {
  return getIcon(
    { name: 'content_copy', type: 'material' },
    'Duplicar',
    'orange',
    true,
    'duplicate'
  );
}

function getEditOrDetailMenuCard(canEdit: boolean, canDetail: boolean): actionMenuCards {
  return {
    label: canEdit ? 'Editar' : 'Ver detalle',
    icon: canEdit
      ? { name: 'edit', type: 'material', class: 'text-mg-main-blue' }
      : { name: 'visibility', type: 'material', class: 'text-mg-main-blue' },
    type: 'submit',
    action: canEdit ? 'update' : 'detail',
    visible: canEdit || canDetail
  };
}

function getDuplicateMenuCard(): actionMenuCards {
  return {
    label: 'Duplicar',
    icon: { name: 'content_copy', type: 'material', class: '!text-mg-orange-dark' },
    type: 'submit',
    action: 'duplicate',
    visible: true
  };
}

function getDetailMenuCards(canDetail: boolean): actionMenuCards[] {
  return canDetail ? [{
    label: 'Ver detalle',
    icon: { name: 'visibility', type: 'material', class: 'text-mg-main-blue' },
    type: 'submit',
    action: 'detail',
    visible: true
  }] : [];
}

function isClosedHourType(sCodigo_Tipo_Horas: string): boolean {
  return sCodigo_Tipo_Horas === TIME_TYPE.EXTRAS.toString() ||
    sCodigo_Tipo_Horas === TIME_TYPE.COMPENSACIÓN.toString() ||
    sCodigo_Tipo_Horas === TIME_TYPE.HORARIO_REGULAR.toString() ||
    sCodigo_Tipo_Horas === TIME_TYPE.RECUPERACIÓN.toString();
}

function setOwnTaskIcons(item: TaskItem, context: TaskRowContext): void {
  const { accessService, sTipo_listado } = context;
  const canEditOwn = accessService.validarPermisoSlug('TASK-UPDATE');
  const canDetailOwn = !canEditOwn && accessService.validarPermisoSlug('TASK-DETAIL');
  item.icObservarTarea = !item.nEstado_Pago ? enabledToObserve(
    context.idUsuario,
    item.nId_Usuario,
    item.nContador_Obs,
    context.TaskObserve,
    Number(item.sEstado),
    sTipo_listado,
    item.revisado,
    item.nUsuario_Revisor
  ) : getEmptyIcon();

  const isApproved = item.sEstado == ACTIVITY_STATUS.APROBADO.toString();
  if (isApproved && item.sCodigo_Tipo_Horas == TIME_TYPE.AVANCE.toString()) {
    item.icEditarTarea = getEditOrDetailIcon(canEditOwn, canDetailOwn);
    item.icDuplicarTarea = getDuplicateIcon();
    item.icEliminarTarea = getEmptyIcon();
    item.actionMenuCards = [getEditOrDetailMenuCard(canEditOwn, canDetailOwn), getDuplicateMenuCard()];
  } else if (isApproved && isClosedHourType(item.sCodigo_Tipo_Horas)) {
    item.icEditarTarea = getDetailIcon(canDetailOwn);
    item.icDuplicarTarea = getDuplicateIcon();
    item.icEliminarTarea = getEmptyIcon();
    item.actionMenuCards = [...getDetailMenuCards(canDetailOwn), getDuplicateMenuCard()];
  } else {
    item.icEditarTarea = getEditOrDetailIcon(canEditOwn, canDetailOwn);
    item.icDuplicarTarea = getDuplicateIcon();
    const canDelete = accessService.validarPermisoSlug('TASK-DELETE');
    item.icEliminarTarea = getIcon(
      { name: 'delete', type: 'material' },
      'Eliminar',
      'red',
      canDelete,
      'delete'
    );
    item.actionMenuCards = [
      getEditOrDetailMenuCard(canEditOwn, canDetailOwn),
      getDuplicateMenuCard(),
      {
        label: 'Eliminar',
        icon: { name: 'delete', type: 'material', class: '!text-mg-red-dark' },
        type: 'submit',
        action: 'delete',
        visible: canDelete
      }
    ];
  }
}

function setListTaskIcons(item: TaskItem, context: TaskRowContext): void {
  const canDetail = !context.canEdit && context.accessService.validarPermisoSlug('TASK-DETAIL');

  setListBaseIcons(item, context);
  if (item.sEstado === ACTIVITY_STATUS.APROBADO.toString()) {
    setApprovedListIcons(item, context, canDetail);
  } else {
    setPendingListIcons(item, context, canDetail);
  }
  applyTeamListOverrides(item, context, canDetail);
  applyAllListOverrides(item, context);
}

function setListBaseIcons(item: TaskItem, context: TaskRowContext): void {
  const { accessService, sTipo_listado, hasTaskDashboardTeam, obsBadge } = context;
  const puedeObservar = sTipo_listado === 'TASK-LIST-ALL'
    ? accessService.validarPermisoSlug('TASK-OBSERVE-ALL')
    : (accessService.validarPermisoSlug('TASK-OBSERVE') || accessService.validarPermisoSlug('TASK-OBSERVE-ALL'));
  item.icObservarTarea = !item.nEstado_Pago && puedeObservar ? getIcon(
    { name: 'add_comment', type: 'material' },
    'Observar',
    'teal',
    true,
    'observe'
  ) : getEmptyIcon();

  if (sTipo_listado == 'TASK-LIST-OWN' || sTipo_listado == 'TASK-LIST-ALL') {
    item.rowDisable = !!item.nId_Revision;
  } else {
    item.rowDisable = false;
  }

  if (obsBadge.icon && !hasTaskDashboardTeam) {
    item.icObservations = getIcon(
      obsBadge.icon,
      'Observar',
      'orange',
      true,
      'observe-individual'
    );
  } else {
    item.icObservations = getEmptyIcon();
  }

  item.icAprobarPago =
    sTipo_listado === 'TASK-LIST-ALL' &&
      item.sEstado === ACTIVITY_STATUS.APROBADO.toString() &&
      item.sCodigo_Tipo_Horas === TIME_TYPE.EXTRAS.toString() &&
      Number(item.nEstado_Pago) == 0
      ? getIcon(
        { name: 'payments', type: 'material' },
        'Aprobar pago',
        'green',
        accessService.validarPermisoSlug('TASK-PAY'),
        'approve-pay'
      )
      : getEmptyIcon();
}

function setApprovedListIcons(item: TaskItem, context: TaskRowContext, canDetail: boolean): void {
  if (context.sTipo_listado === 'TASK-LIST-ALL') {
    if (item.sCodigo_Tipo_Horas == TIME_TYPE.AVANCE.toString()) {
      item.icEditarTarea = getEditOrDetailIcon(context.canEdit, canDetail);
      item.actionMenuCards = [getEditOrDetailMenuCard(context.canEdit, canDetail)];
    } else {
      item.icEditarTarea = getDetailIcon(canDetail);
      item.actionMenuCards = [...getDetailMenuCards(canDetail)];
    }
  } else {
    item.icEditarTarea = getDetailIcon(canDetail);
  }
  item.icAprobarTarea = getEmptyIcon();
}

function setPendingListIcons(item: TaskItem, context: TaskRowContext, canDetail: boolean): void {
  const { sTipo_listado, hasTaskDashboardTeam } = context;
  const isNotRegularHours = item.sCodigo_Tipo_Horas !== TIME_TYPE.HORARIO_REGULAR.toString();
  const isTeamOrAllList = sTipo_listado === 'TASK-LIST-TEAM' || sTipo_listado === 'TASK-LIST-ALL';
  const shouldHideEdit = isNotRegularHours && isTeamOrAllList;

  item.icEditarTarea = getEditableIcon(item, context, canDetail, shouldHideEdit);

  item.icAprobarTarea = !hasTaskDashboardTeam ? enabledToApprove(
    Number(item.sEstado),
    Number(item.sCodigo_Tipo_Horas),
    context.TaskApprove,
    sTipo_listado,
    item.revisado,
    item.nUsuario_Revisor,
    getApproverIds(item.sAprobadores_Solicitud),
    context.showApprovalButton,
  ) : getEmptyIcon();

  if (isTeamOrAllList) {
    item.actionMenuCards = [getEditOrDetailMenuCard(context.canEdit, canDetail)];
  }
}

function getEditableIcon(item: TaskItem, context: TaskRowContext, canDetail: boolean, shouldHideEdit: boolean): IconBadge {
  return !context.hasTaskDashboardTeam && !shouldHideEdit ? enabledToEdit(
    context.nIdColaborador,
    item.nId_Usuario,
    Number(item.sEstado),
    context.sTipo_listado,
    context.TaskEdit,
    Number(item.revisado),
    Number(item.sCodigo_Tipo_Horas),
    item.nUsuario_Revisor
  ) : getDetailIcon(canDetail);
}

function getApproverIds(sAprobadores_Solicitud: string): number[] {
  if (!(sAprobadores_Solicitud && sAprobadores_Solicitud.includes('['))) return [0];
  const aprobadores: { id: number }[] = JSON.parse(sAprobadores_Solicitud);
  return aprobadores.length ? aprobadores.map((aprobador) => aprobador.id) : [0];
}

function applyTeamListOverrides(item: TaskItem, context: TaskRowContext, canDetail: boolean): void {
  const { accessService, sTipo_listado, hasTaskDashboardTeam } = context;
  if (sTipo_listado != 'TASK-LIST-TEAM') return;

  const isNotRegularHours = item.sCodigo_Tipo_Horas !== TIME_TYPE.HORARIO_REGULAR.toString();
  const isApproved = item.sEstado === ACTIVITY_STATUS.APROBADO.toString();

  if (isNotRegularHours && !hasTaskDashboardTeam) {
    item.icObservarTarea = getIcon(
      { name: 'add_comment', type: 'material' },
      'Observar',
      'teal',
      accessService.validarPermisoSlug('TASK-OBSERVE'),
      'observe'
    );
    item.icEditarTarea = getDetailIcon(canDetail);
  }

  if (isApproved) {
    item.icAprobarTarea = getEmptyIcon();
    item.icEditarTarea = getEditableIcon(item, context, canDetail, isNotRegularHours);
  } else {
    const puedeAprobar = (accessService.validarPermisoSlug('TASK-APPROVE') || accessService.validarPermisoSlug('TASK-APPROVE-ALL')) && context.nIdColaborador !== item.nId_Colaborador;
    item.icAprobarTarea = getIcon(
      { name: 'check_circle', type: 'material' },
      'Aprobar',
      'green',
      puedeAprobar,
      'approve'
    );
  }

  if (context.canEdit && !isApproved) {
    item.icEditarTarea = getEditIcon();
  }
}

function applyAllListOverrides(item: TaskItem, context: TaskRowContext): void {
  if (context.sTipo_listado != 'TASK-LIST-ALL' || item.sEstado === ACTIVITY_STATUS.APROBADO.toString()) return;

  if (context.canEdit) {
    item.icEditarTarea = getEditIcon();
    item.actionMenuCards = [
      {
        label: 'Editar',
        icon: { name: 'edit', type: 'material', class: 'text-mg-main-blue' },
        type: 'submit',
        action: 'update',
        visible: true
      },
    ];
  }

  if (context.accessService.validarPermisoSlug('TASK-APPROVE-ALL')) {
    item.icAprobarTarea = getIcon(
      { name: 'check_circle', type: 'material' },
      'Aprobar',
      'green',
      context.nIdColaborador !== item.nId_Colaborador,
      'approve'
    );
  }
}


export function transformTaskList(
  response: GenericResponseList<TaskData>,
  _accessService: AccessService,
  sTipo_listado: string,
  errors: TaskData[]
) {
  if (!response.data) response.data = [];
  let nMinutosRegular = 0;
  let nMinutosSobretiempo = 0;
  const hasTaskDashboardTeam = _accessService.validarPermisoSlug("INDICATOR-TASK-TEAM");

  (response.data as TaskItem[]).forEach((task) => mapTaskRow(task, _accessService, sTipo_listado, errors, hasTaskDashboardTeam));
  (response.data as TaskItem[]).forEach((task) => {
    const minutos = Number(task.nMinutos) || 0;

    switch (+task.sCodigo_Tipo_Horas) {
      case TIME_TYPE.HORARIO_REGULAR:
        nMinutosRegular += minutos;
        break;
      case TIME_TYPE.EXTRAS:
        nMinutosSobretiempo += minutos;
        break;
      case TIME_TYPE.COMPENSACIÓN:
        nMinutosSobretiempo += minutos;
        break;
      case TIME_TYPE.AVANCE:
        nMinutosSobretiempo += minutos;
        break;
      case TIME_TYPE.RECUPERACIÓN:
        nMinutosSobretiempo += minutos;
        break;
    }
  });
  const nMinutosTotal = nMinutosRegular + nMinutosSobretiempo;
  response.footer = {
    totalHoras: setTotalHours(nMinutosTotal),
    horasRegulares: setTotalHours(nMinutosRegular),
    horasSobretiempo: setTotalHours(nMinutosSobretiempo),
    nMinutosRegular,
    nMinutosSobretiempo,
    nMinutosTotal,
  };

  return response;
}

export function mapTareoResponse(
  data: any[],
  idUsuario: number,
  TaskApprove: boolean,
  TaskObserve: boolean,
  typoListado: string[]
): any[] {
  return data.map((item: any) => {
    const taskDelete =
      item.nId_Usuario == idUsuario
        ? true
        : false;

    item.nId_Falso = 0;

    const name_pic: NameWithPic = {
      urlPic: item.sUrl_Foto,
      primaryText: { text: item.sNombre_Colaborador },
    };

    item.NameWithPic = getTextWithPic(name_pic);

    if (item.sNombre_Tipo_Horas != 'HORARIO REGULAR') {
      item.badgeHFinHini = obteniendoBadgeHfinHini(
        item.dFecha_Fin,
        item.dFecha_Registro,
        item.dHora_Fin,
        item.dHora_Inicio
      );
    } else {
      item.badgeHFinHini = obteniendoBadgeHfinHini(null, null, null, null);
    }

    item.badgeRango = item.badgeHFinHini.data
      ? getDateBagde(
        item.badgeHFinHini.data.dInicio,
        item.badgeHFinHini.data.dFin,
        true
      )
      : getDateBagde('', '', false);

    item.total = getRoundBadge(
      item.nCantidad_tareas !== 1
        ? item.nCantidad_tareas.toString() + ' Actividades'
        : item.nCantidad_tareas.toString() + ' Actividad',
      item.nCantidad_tareas > 99 ? '+99' : item.nCantidad_tareas.toString()
    );

    item.totalHours = getSimpleBadge(setDecimalHours(item.nMin_Total), undefined, 'blue');
    item.regular = getSimpleBadge(setDecimalHours(item.nMin_Regulares));
    item.extras = getSimpleBadge(setDecimalHours(item.nMin_Extras));
    item.compensacion = getSimpleBadge(setDecimalHours(item.nMin_Compensacion));
    item.recuperacion = getSimpleBadge(setDecimalHours(item.nMin_Recuperacion));
    item.avance = getSimpleBadge(setDecimalHours(item.nMin_Avance));
    item.aprobadas = getSimpleBadge(setDecimalHours(item.nMin_Aprobados), undefined, 'green');
    item.pendientes = getSimpleBadge(setDecimalHours(item.nMin_Pendientes), undefined, 'orange');
    item.pendDirector = getSimpleBadge(setDecimalHours(item.nMin_Pendientes_Director), undefined, 'orange');

    item.badgeDateWithIconHistoric = getArrayWithBadgeAndIcon(
      {
        label: moment(item.dFecha_Registro).format('DD MMM YYYY'),
        tooltip: '',
        color: 'main',
        label2: moment(item.dFecha_Registro).format('dddd'),
      },
      item.nContador_Historico
        ? {
          icon: {
            type: 'material',
            name: 'history',
          },
          tooltip: 'Histórico de cambios',
          action: 'tareo-historic',
          typeAction: 'modal',
          color: COLORS_BADGE.main,
        }
        : null
    );

    item.icEliminarTarea = enabledToDelete(
      item.sEstado,
      taskDelete,
      typoListado[0],
      item.revisado
    );

    item.icDuplicarTarea =
      idUsuario == item.nId_Usuario
        ? getIconDuplicate(true, item.sEstado, typoListado[0], item.revisado)
        : false;

    item.icAprobarTarea = enabledToApprove(
      item.sEstado,
      item.sCodigo_Tipo_Horas,
      TaskApprove,
      typoListado[0],
      item.revisado,
      item.nUsuario_Revisor,
      item.sAprobadores_Solicitud && item.sAprobadores_Solicitud.includes('[')
        ? JSON.parse(item.sAprobadores_Solicitud)[0]?.id ?? 0
        : 0
    );

    item.icObservarTarea = !item.nEstado_Pago
      ? enabledToObserve(
        idUsuario,
        item.nId_Usuario,
        item.nContador_Obs,
        TaskObserve,
        item.sEstado,
        typoListado[0],
        item.revisado,
        item.nUsuario_Revisor
      )
      : '';

    item.icObservations = showObserver(item.nContador_Obs, typoListado[0], item.revisado);

    item.btnAprobarTarea = enabledToApprove(
      item.sEstado,
      item.sCodigo_Tipo_Horas,
      TaskApprove,
      typoListado[0],
      item.revisado,
      item.nUsuario_Revisor,
      item.sAprobadores_Solicitud && item.sAprobadores_Solicitud.includes('[')
        ? JSON.parse(item.sAprobadores_Solicitud)[0]?.id ?? 0
        : 0
    );

    return item;
  });
}

export const mapDataToFilterIndicator = (resp: any[], sIdPrincipal: string, arrayName: string, Estate_Property: string, Estate_SubProperty?: string) => {
  let response: any[] = []
  if (resp.length) {
    resp
      .filter((item: any) => item[arrayName].length > 0)
      .map((item: any) => {
        item[`s${Estate_Property}`] = { ...getPryRqState(item[`n${Estate_Property}`]), value: item[`n${Estate_Property}`] }
        item.isExpanded = false;
        item.nMin_Restantes = 0
        item.nMin_Aprobadas = 0
        item.nMin_Pendiente = 0
        item.nMin_Estimadas = 0
        item[arrayName].map((x: any) => {
          x.nMin_Restantes = x.nMin_Estimadas - x.nMin_Aprobadas - x.nMin_Pendiente
          item.nMin_Restantes = item.nMin_Restantes + x.nMin_Restantes
          item.nMin_Aprobadas = item.nMin_Aprobadas + x.nMin_Aprobadas
          item.nMin_Pendiente = item.nMin_Pendiente + x.nMin_Pendiente
          item.nMin_Estimadas = item.nMin_Estimadas + x.nMin_Estimadas

          if (typeof x[`n${Estate_SubProperty}`] == 'number') {
            x[`s${Estate_SubProperty}`] = { ...getPryRqState(x[`n${Estate_SubProperty}`]), value: x[`n${Estate_SubProperty}`] }
          }
          x.nMin_Restantes = getHourBadge(x.nMin_Restantes);
          x.nId_Property = item[sIdPrincipal]
          x.nMin_Aprobadas = getHourBadge(x.nMin_Aprobadas);
          x.tooltipPendientes = setOvertimeTooltip(x.nMin_Pendiente)
          if (x.dFecha_Inicio_Pendiente) x.tooltipPendientes = moment(x.dFecha_Inicio_Pendiente).format('DD-MM-YY');
          if (x.dFecha_Fin_Pendiente) x.tooltipPendientes = moment(x.dFecha_Fin_Pendiente).format('DD-MM-YY')
          if (x.dFecha_Fin_Pendiente && x.dFecha_Inicio_Pendiente) x.tooltipPendientes = `Desde ${moment(x.dFecha_Inicio_Pendiente).format('DD-MM-YY')} hasta ${moment(x.dFecha_Fin_Pendiente).format('DD-MM-YY')}`
          x.nMin_Pendiente = getSimpleBadge(
            String(setHours(x.nMin_Pendiente)),
            x.tooltipPendientes,
            'orange')
          x.nMin_Estimadas = getHourBadge(x.nMin_Estimadas)
        })

        item.tooltipPendientes = setOvertimeTooltip(item.nMin_Pendiente)
        if (item.dFecha_Inicio_Pendiente) item.tooltipPendientes = moment(item.dFecha_Inicio_Pendiente).format('DD-MM-YY');
        if (item.dFecha_Fin_Pendiente) item.tooltipPendientes = moment(item.dFecha_Fin_Pendiente).format('DD-MM-YY');
        if (item.dFecha_Inicio_Pendiente && item.dFecha_Fin_Pendiente) item.tooltipPendientes = `Desde ${moment(item.dFecha_Inicio_Pendiente).format('DD-MM-YY')} hasta ${moment(item.dFecha_Fin_Pendiente).format('DD-MM-YY')}`

        item.nMin_Aprobadas = { ...getHourBadge(item.nMin_Aprobadas), nMin_Aprobadas: item.nMin_Aprobadas };
        item.nMin_Estimadas = { ...getHourBadge(item.nMin_Estimadas), nMin_Estimadas: item.nMin_Estimadas };
        item.nMin_Pendiente = {
          ...getSimpleBadge(
            String(setHours(item.nMin_Pendiente)),
            item.tooltipPendientes,
            'orange'), nMin_Pendiente: item.nMin_Pendiente
        }
        item.nMin_Restantes = { ...getHourBadge(item.nMin_Restantes), nMin_Restantes: item.nMin_Restantes };

        response.push(item)
      })
  }
  return response
}

const getPryRqState = (nState: number) => {
  switch (nState) {
    case PROJECTS_STATUS.ACTIVO:
      return getSimpleBadge('ACTIVO', 'ACTIVO', 'green', '!font-bold')
    case PROJECTS_STATUS.INACTIVO:
      return getSimpleBadge('INACTIVO', 'INACTIVO', 'main', '!font-bold')
    case PROJECTS_STATUS.PAUSADO:
      return getSimpleBadge('PAUSADO', 'PAUSADO', 'yellow', '!font-bold')
    case PROJECTS_STATUS.COMPLETADO:
      return getSimpleBadge('COMPLETADO', 'COMPLETADO', 'cyan', '!font-bold')
    case PROJECTS_STATUS.DESESTIMADO:
      return getSimpleBadge('DESESTIMADO', 'DESESTIMADO', 'red', '!font-bold')
    default:
      return ''
  }
}

const getHourBadge = (min: number) => {
  return getSimpleBadge(String(setHours(min)), setOvertimeTooltip(min), setColor(min))
}

export const setOvertimeTooltip = (min: number) => {
  let sHours = ''
  if (min < 0) {
    let newMin = min * -1
    sHours = setTotalHours(newMin)
    return `Hay ${sHours} de sobretiempo`
  } else {
    return String(setHours(min))
  }
}

const setHours = (args: number) => {
  if (args < 0) {
    let value = args * -1
    return `-${setDecimalHours(value)}`
  } else {
    return setDecimalHours(args)
  }
}

const setColor = (min: number) => {
  if (min < 0) {
    return 'red'
  } else return 'main'
}

export function parseInputs(getInputs: string): Record<string, any> {
  if (!getInputs) return {};

  return getInputs
    .replace(/^\?/, '')
    .split('&')
    .reduce((acc, pair) => {
      const [key, value = ''] = pair.split('=');

      const decodedValue = decodeURIComponent(value);

      acc[key] =
        decodedValue === 'true' ? true : decodedValue === 'false' ? false : decodedValue;
      return acc;
    }, {} as Record<string, any>);
}


/* Mapeo del footer */
export function transformFooterData(
  response: GenericResponse<FooterTotals>
): GenericResponse<FooterTotals> {
  if (!response?.isSuccess || !response.data) {
    response.data = null as any;
    return response;
  }

  const {
    nMinutosRegular,
    nMinutosExtras,
    nMinutosRecuperacion,
    nMinutosCompensacion,
    nMinutosAvance,
  } = response.data as any;

  const nMinutosSobretiempo =
    nMinutosExtras +
    nMinutosCompensacion +
    nMinutosRecuperacion +
    nMinutosAvance;

  const nMinutosTotal = nMinutosRegular + nMinutosSobretiempo;

  response.data = {
    totalHoras: setTotalHours(nMinutosTotal),
    horasRegulares: setTotalHours(nMinutosRegular),
    horasSobretiempo: setTotalHours(nMinutosSobretiempo),
    nMinutosRegular,
    nMinutosSobretiempo,
    nMinutosTotal,
  };

  return response;
}

export function getEmptyIcon(): any {
  return {
    icon: '',
    tooltip: '',
    color: '',
    visible: false,
    action: ''
  };
}

export const enableToPay = (task: any, typeList: string, permission: boolean) => {
  let generalCss = 'flex justify-center cursor-pointer items-center p-1.5 w-fit rounded-full ';
  let iconObj: IconBadge = {
    tooltip: 'Enviar a pago',
    icon: null,
    css: '',
    action: null,
  };
  if (typeList == 'TASK-LIST-ALL' && !task.nEstado_Pago && Number(task.sEstado) === ACTIVITY_STATUS.APROBADO && Number(task.sCodigo_Tipo_Horas) === TIME_TYPE.EXTRAS) {
    iconObj = {
      tooltip: 'Enviar a pago',
      icon: {
        name: 'payments',
        type: 'material'
      },
      css: generalCss + COLORS_BADGE.green_dark,
      action: null,
    };
  }
  return permission ? iconObj : null
}

export function toQueryString(params: Record<string, any>): string {
  return Object.keys(params)
    .filter(key => params[key] !== null && params[key] !== undefined)
    .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
    .join('&');
}

//* Mapeo de data para initRevisionTask
export function normalizeInitFilters(filters: any) {
  return {
    numPage: 1,
    order: filters.sortDir ?? "desc",
    sort: filters.sortBy ?? "nId",
    records: 0,
    numFilter: filters.numFilter ?? null,
    textFilter: filters.textFilter ?? null,
    sTipo_listado: filters.listType ?? null,
    sFilterOne: filters.filterOne ?? null,
    sFilterTwo: filters.filterTwo ?? null,
    sFilterThree: filters.filterThree ?? null,
    sFilterFour: filters.filterFour ?? null,
    sFilterFive: filters.filterFive ?? null,
    sFilterSix: filters.filterSix ?? null,
    sFilterSeven: filters.filterSeven ?? null,
    sFilterEight: filters.filterEight ?? null,
    sFilterNine: filters.filterNine ?? null,
    dFecha_Inicio: filters.startDate ?? null,
    dFecha_Fin: filters.endDate ?? null,
    minDate: filters.minDate ?? null,
    maxDate: filters.maxDate ?? null,
    bDownload: false,
    bListado_individual: filters.isIndividualListing ?? false,
    sFilterNotification: null,
  };
}
